import { Card, CardContent, Typography } from "@mui/material";
import React, { useState } from "react";
import Seperator from "../../Atoms/Seperator";
import ProviderComponent from "../../Atoms/ProviderComponent";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { capitalizeFirstLetter } from "../../../util";
import SimpleMenuItems from "../../Atoms/SimpleMenuItems";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useSolution } from "../../../contexts/SolutionsContext";
import { PUBLISHED_TXT } from "../../../utils/constants";
import { useDashboard } from "../../../contexts/DashboardContext";
import CustomTooltip from "../../Atoms/CustomTooltip";
import ReactMarkdown from "react-markdown";
import usePermissions from "../../../hooks/userPermissions";
import "./solution.card.css";

const SolutionsCard = ({ solution }) => {
  const {
    selectSolution,
    setSolutionCardOpen,
    fetchSolutionVersions,
    deleteSolution,
    changeSolutionDrawerState,
  } = useSolution();

  const { canSolutionRead, canSolutionUpdate, canSolutionDelete } =
    usePermissions();

  const { showConfirmationModal } = useDashboard();

  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [cardAnchorEl, setCardAnchorEl] = useState(null);
  const [cardIndustryPopupEl, setCardIndustryPopupEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    event?.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (event) => {
    event?.stopPropagation();
    showConfirmationModal({
      title: "Edit Solution",
      body: "Are you sure you want to edit this Solution?",
      onOkLabel: "Confirm",
      onOk: handleEditConfirm,
    });
    handleMenuClose();
  };
  const handleEditConfirm = async () => {
    if (solution?.solution_id) {
      navigate(`edit-solution/${solution?.solution_id}`);
    } else {
      // Handle case where solution_id is not available
    }
  };

  const handleDelete = (event) => {
    event?.stopPropagation();
    showConfirmationModal({
      title: "Delete Solution",
      body: "Are you sure you want to delete solution?",
      onOkLabel: "Confirm",
      onOk: handleDeleteConfirm,
    });
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    await deleteSolution(solution?.solution_id);
  };

  const menuItems = [
    { label: "Edit", onClickFn: handleEdit, hidden: !canSolutionUpdate },
    { label: "Delete", onClickFn: handleDelete, hidden: !canSolutionDelete },
  ];

  const handleHover = (e) => {
    if (e.currentTarget.dataset.name === "skillsTooltip") {
      setCardAnchorEl(e.currentTarget);
    } else if (e.currentTarget.dataset.name === "industryTooltip") {
      setCardIndustryPopupEl(e.currentTarget);
    }
  };

  const handleClose = (e) => {
    if (e.currentTarget.dataset.name === "skillsTooltip") {
      setCardAnchorEl(null);
    } else if (e.currentTarget.dataset.name === "industryTooltip") {
      setCardIndustryPopupEl(null);
    }
  };

  const industryInfo = solution?.solution_metadata?.a2a?.industry_info;

  return (
    <>
      <Card
        className="solutionCard"
        onClick={async () => {
          if (canSolutionRead) {
            selectSolution(solution);
            setSolutionCardOpen(true);
            changeSolutionDrawerState(true);
            await fetchSolutionVersions(solution?.solution_id);
          }
        }}
        sx={{
          cursor: "pointer",
          borderRadius: "8px",
          boxShadow: "5px 4px 4px 0px var(--db-secondary-white-two)",
          overflow: "initial",
          position: "relative",
        }}
      >
        <CardContent
          sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <div>
            <div className="solutionCardHeader ">
              <Typography className="solutionCardTitle" component="div">
                {solution?.solution_name || ""}
              </Typography>
            </div>

            <div
              className="solutionCardStatus"
              style={{
                color:
                  solution?.approval_status &&
                  solution?.approval_status?.toLowerCase() ===
                    PUBLISHED_TXT?.toLowerCase()
                    ? "var(--db-primary-green-four)"
                    : "var(--db-secondary-gray-seven)",
              }}
            >
              {capitalizeFirstLetter(solution?.approval_status || "")}
            </div>
          </div>
          <div className="solutionCardDesc" variant="body2">
            <ReactMarkdown>
              {(solution?.description || "").replace(/<\/?u>/g, "")}
            </ReactMarkdown>
          </div>
          <Seperator
            styles={{
              border: 0,
              borderTop: "1px solid var(--db-secondary-gray-four)",
              strokeWidth: "O.5px",
              margin: "0",
            }}
          />
          <div spacing={1} style={{ height: "50px", position: "relative" }}>
            {industryInfo && industryInfo?.length > 0 ? (
              <>
                {industryInfo?.slice(0, 2).map((item, index) => {
                  const industry = item?.industry || "";
                  const sectors = item?.sector || [];

                  return (
                    <div key={index}>
                      <div>
                        <div
                          className="mb-2"
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            width: "99%",
                          }}
                        >
                          <div
                            className="sol-industries-sector-div"
                            style={{
                              maxWidth: `${
                                index === 1 && industryInfo?.length > 2
                                  ? "80%"
                                  : "auto"
                              }`,
                            }}
                          >
                            {industry}
                            {sectors && sectors?.length > 0 && (
                              <> | {sectors?.join(", ")}</>
                            )}
                          </div>
                          {index === 1 && industryInfo.length > 2 && (
                            <div
                              className="solutionCardMoreCount ms-2"
                              onMouseOver={handleHover}
                              onMouseLeave={handleClose}
                              data-name="industryTooltip"
                              onClick={(e) => {
                                e?.stopPropagation();
                              }}
                            >
                              +
                              {solution?.solution_metadata?.a2a?.industry_info
                                .length - 2}{" "}
                              more
                            </div>
                          )}
                          {cardIndustryPopupEl && (
                            <div className="industryTooltip-hover">
                              <CustomTooltip
                                containerClsNames="marketplace-tooltip-container"
                                txtClsNames="marketplace-tooltip-txt"
                                arrowClsNames="marketplace-tooltip-arrow-ind-sec"
                                tooltipTitle="Industries and Sectors"
                                content={
                                  <ul>
                                    {industryInfo?.map((item, index) => (
                                      <li key={index}>
                                        {item?.industry} |{" "}
                                        {item?.sector?.join(", ")}
                                      </li>
                                    ))}
                                  </ul>
                                }
                                tooltipTitleClass="mas-tooltip-title"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                <div className="solutionCardIndustry">
                  No industry information available
                </div>
              </>
            )}
          </div>

          <div className="wd-100 d-flex">
            <ProviderComponent
              containerClassName={"wd-50 pe-2"}
              title={"No. of Agents"}
              description={solution?.agents?.length || 0}
              titleClassName={"titleClass"}
              textClassName={"textClass ellipsis-text"}
            />
            <ProviderComponent
              containerClassName={"wd-50 pe-2"}
              title={"Provider"}
              description={solution?.solution_metadata?.a2a?.provider || ""}
              titleClassName={"titleClass"}
              textClassName={"textClass ellipsis-text"}
            />
          </div>
          <div className="wd-100 d-flex">
            <ProviderComponent
              containerClassName={"wd-50 pe-2"}
              title={"Point of Contact"}
              description={solution?.point_of_contact || ""}
              titleClassName={"titleClass"}
              textClassName={"textClass ellipsis-text"}
            />
            <ProviderComponent
              containerClassName={"wd-50 pe-2"}
              title={"Created on"}
              description={format(solution?.created_on, "MM/dd/yy")}
              titleClassName={"titleClass"}
              textClassName={"textClass ellipsis-text"}
            />
          </div>
        </CardContent>

        {(canSolutionUpdate || canSolutionDelete) && (
          <div className="sol-menuVertIcon" onClick={handleMenuClick}>
            <MoreVertIcon style={{ cursor: "pointer" }} />
            <SimpleMenuItems
              items={menuItems}
              open={open}
              onClose={handleMenuClose}
            />
          </div>
        )}
      </Card>
    </>
  );
};

export default SolutionsCard;
